/*
 * Related Products Carousel
 * Inherits all functionality from carousel.js but isolated to prevent conflicts
 */

// Related Products Slider Component - Full carousel.js functionality
if (!customElements.get('related-products-slider')) {
  class RelatedProductsSlider extends HTMLElement {
    constructor() {
      super();
      this.carouselElement = this;
      this.splide = null;
      this.carouselSync = null;
      this.isInitialized = false;
  
      // Parse all data attributes - matching carousel.js pattern
      this.desktopPerPage = parseInt(this.carouselElement.dataset['desktopPerPage']) || parseInt(this.carouselElement.dataset['desktopperpage']) || 4;
      this.mobilePerPage = parseInt(this.carouselElement.dataset['mobilePerPage']) || parseInt(this.carouselElement.dataset['mobileperpage']) || 1;
      this.desktopPerMove = parseInt(this.carouselElement.dataset['desktopPerMove']) || parseInt(this.carouselElement.dataset['desktoppermove']) || 1;
      this.mobilePerMove = parseInt(this.carouselElement.dataset['mobilePerMove']) || parseInt(this.carouselElement.dataset['mobilepermove']) || 1;
      this.focus = this.carouselElement.dataset['focus'];
      this.type = this.carouselElement.dataset['type'];
      this.gap = this.carouselElement.dataset['gapDesktop'] || this.carouselElement.dataset['gapbetweenslides'] || '0';
      this.gapMobile = this.carouselElement.dataset['gapMobile'] || this.carouselElement.dataset['gapbetweenslidesonmobile'] || '0';
      this.autoplaySpeed = parseInt(this.dataset['autoplaySpeed']) || parseInt(this.dataset['autoplay-speed']) || 3000;
      this.direction = this.carouselElement.dataset['direction'] || 'ltr';
      this.mobilePaddingLeft = this.carouselElement.dataset['mobilePaddingLeft'] || this.carouselElement.dataset['mobilepaddingleft'] || '0';
      this.mobilePaddingRight = this.carouselElement.dataset['mobilePaddingRight'] || this.carouselElement.dataset['mobilepaddingright'] || '0';
      this.desktopPaddingLeft = this.carouselElement.dataset['desktopPaddingLeft'] || this.carouselElement.dataset['desktoppaddingleft'] || '0';
      this.desktopPaddingRight = this.carouselElement.dataset['desktopPaddingRight'] || this.carouselElement.dataset['desktoppaddingright'] || '0';
      this.mobileFixedWidth = this.carouselElement.dataset['mobileFixedWidth'] || this.carouselElement.dataset['mobilefixedwidth'];
      this.desktopFixedWidth = this.carouselElement.dataset['desktopFixedWidth'] || this.carouselElement.dataset['desktopfixedwidth'];
  
      // Boolean attributes - matching carousel.js pattern
      this.showarrows = this.carouselElement.dataset['showArrows'] === 'true' || this.carouselElement.dataset['showarrows'] === 'true' || false;
      this.autoplay = this.carouselElement.dataset['autoplay'] === 'true' || false;
      this.showarrowsonmobile = this.carouselElement.dataset['showArrowsMobile'] === 'true' || this.carouselElement.dataset['showarrowsonmobile'] === 'true' || false;
      this.showdots = this.carouselElement.dataset['showDots'] === 'true' || this.carouselElement.dataset['showdots'] === 'true' || false;
      this.showdotsonmobile = this.carouselElement.dataset['showDotsMobile'] === 'true' || this.carouselElement.dataset['showdotsonmobile'] === 'true' || false;
      this.isNavigation = this.carouselElement.dataset['isNavigation'] === 'true' || this.carouselElement.dataset['isnavigation'] === 'true' || false;
      this.disableDrag = this.carouselElement.dataset['disableDrag'] === 'true' || this.carouselElement.dataset['disabledrag'] === 'true' || false;
      this.destroyOnMobile = this.carouselElement.dataset['enableOnMobile'] == 'false' || this.carouselElement.dataset['enableonmobile'] == 'false' || false;
      this.destroyOnDesktop = this.carouselElement.dataset['enableOnDesktop'] == 'false' || this.carouselElement.dataset['enableondesktop'] == 'false' || false;
      this.omitEnd = this.carouselElement.dataset['omitEnd'] == 'false' || false;
      this.refresh = this.carouselElement.dataset['refresh'] == 'true' || false;
      this.sync = this.carouselElement.dataset['carouselSyncSelector'] || this.carouselElement.dataset['carouselsyncselector'] || false;

      // Ensure gap values have proper units if they're not 0
      if (this.gap !== '0' && !this.gap.includes('rem') && !this.gap.includes('px')) {
        this.gap = this.gap + 'rem';
      }
      if (this.gapMobile !== '0' && !this.gapMobile.includes('rem') && !this.gapMobile.includes('px')) {
        this.gapMobile = this.gapMobile + 'rem';
      }

      // Ensure padding values have proper units if they're not 0
      if (this.mobilePaddingLeft !== '0' && !this.mobilePaddingLeft.includes('%') && !this.mobilePaddingLeft.includes('px')) {
        this.mobilePaddingLeft = this.mobilePaddingLeft + '%';
      }
      if (this.mobilePaddingRight !== '0' && !this.mobilePaddingRight.includes('%') && !this.mobilePaddingRight.includes('px')) {
        this.mobilePaddingRight = this.mobilePaddingRight + '%';
      }
      if (this.desktopPaddingLeft !== '0' && !this.desktopPaddingLeft.includes('%') && !this.desktopPaddingLeft.includes('px')) {
        this.desktopPaddingLeft = this.desktopPaddingLeft + '%';
      }
      if (this.desktopPaddingRight !== '0' && !this.desktopPaddingRight.includes('%') && !this.desktopPaddingRight.includes('px')) {
        this.desktopPaddingRight = this.desktopPaddingRight + '%';
      }

      // Carousel element should have class splide
      if (!this.carouselElement.classList.contains('splide')) return;
    }

    connectedCallback() {
      // Don't initialize here - wait for recommendations to load
    }

    init() {
      // Prevent multiple initializations
      if (this.isInitialized || this.splide) {
        return;
      }

      // Check if slides exist
      const slides = this.querySelectorAll('.splide__slide');
      if (slides.length === 0) {
        return;
      }

      this.initCarousel();
    }
   
    initCarousel() {
      if (this.destroyOnMobile && this.destroyOnDesktop) return;

      // Clear existing pagination
      this.clearPagination();

      // Build configuration matching carousel.js structure exactly
      const config = {
        perPage: this.desktopPerPage,
        ...(this.type ? { type: this.type } : {}),
        ...(this.focus ? { focus: this.focus } : {}),
        autoplay: this.autoplay,
        interval: this.autoplaySpeed,
        gap: this.gap,
        arrows: this.showarrows,
        pagination: this.showdots,
        isNavigation: this.isNavigation,
        drag: !this.disableDrag,
        direction: this.direction,
        destroy: this.destroyOnDesktop,
        padding: { left: this.desktopPaddingLeft, right: this.desktopPaddingRight },
        omitEnd: this.omitEnd,
        breakpoints: {
          750: {
            padding: { left: this.mobilePaddingLeft, right: this.mobilePaddingRight },
            perPage: this.mobilePerPage,
            arrows: this.showarrowsonmobile,
            pagination: this.showdotsonmobile,
            gap: this.gapMobile,
            destroy: this.destroyOnMobile,
          }
        },
      };

      // Initialize Splide
      try {
        this.splide = new Splide(this.carouselElement, config);
  
        if (this.sync) {
          this.initProgressBar(this.splide);
          this.initCarouselSync();
          this.splide.refresh();
        } else {
          this.initProgressBar(this.splide);
          this.splide.mount();

          if (this.refresh) {
            this.splide.refresh();
          }
        }

        this.isInitialized = true;
        this.classList.add('is-initialized');
        
        // Dispatch custom event for successful initialization
        this.dispatchEvent(new CustomEvent('slider:initialized', {
          bubbles: true,
          detail: { slider: this.splide }
        }));
      } catch (error) {
        console.error('Related Products Slider Error:', error);
      }
    }

    initProgressBar(skeletonSplide) {
      const bar = this.querySelector('.my-slider-progress-bar');

      if (!bar) return;

      this.splide.on('mounted move', function () {
        var end = skeletonSplide.Components.Controller.getEnd() + 1;
        var rate = Math.min((skeletonSplide.index + 1) / end, 1);
        bar.style.width = String(100 * rate) + '%';
      });
    }
  
    initCarouselSync() {
      this.syncElement = document.querySelector(this.sync);
      if (!this.syncElement) return;

      this.direction = this.syncElement.dataset['direction'] || 'ltr';
  
      this.carouselSync = new Splide(this.sync, {
        updateOnMove: true,
        perPage: this.syncElement.dataset['desktopperpage'] || 5,
        type: this.syncElement.dataset['type'] || 'loop',
        focus: this.syncElement.dataset['focus'] || 'center',
        isNavigation: this.syncElement.dataset['isnavigation'] === 'true' || false,
        pagination: false,
        arrows: this.syncElement.dataset['showarrows'] === 'true' || false,
        drag: true,
        // Conditional configuration for 'ttb' direction
        ...(this.direction === 'ttb' && {
          height: this.getPrimarySlideFirstImageHeight(),
          direction: 'ttb',
          autoHeight: true,
        }),
        breakpoints: {
          767: {
            height: 'auto',
            direction: 'ltr',
            gap: this.gapMobile
          }
        },
      });
  
      this.splide.sync(this.carouselSync);
      this.splide.mount();
      this.carouselSync.mount();
    }
  
    refreshSlider() {
      if (this.splide) {
        this.splide.refresh();
      }
      if (this.carouselSync) {
        this.carouselSync.refresh();
      }
    }

    getPrimarySlideFirstImageHeight() {
      const image = this.querySelector('.splide__list img');
      if (!image) return 'auto';

      const containerWidth = this.querySelector('.splide__list').clientWidth;
  
      // Get the original width and height of the image
      const originalWidth = image.getAttribute('width');
      const originalHeight = image.getAttribute('height');

      if (!originalWidth || !originalHeight) return 'auto';
  
      // Calculate the scaled height to fit within the container
      return (containerWidth / originalWidth) * originalHeight;
    }

    clearPagination() {
      const paginationContainer = this.querySelector('.splide__pagination');
      if (paginationContainer) {
        paginationContainer.innerHTML = '';
      }
    }

    destroy() {
      if (this.splide) {
        try {
          this.splide.destroy();
        } catch (e) {
          // Silent fail
        }
        this.splide = null;
      }
      if (this.carouselSync) {
        try {
          this.carouselSync.destroy();
        } catch (e) {
          // Silent fail
        }
        this.carouselSync = null;
      }
      this.isInitialized = false;
      this.classList.remove('is-initialized');
    }

    disconnectedCallback() {
      this.destroy();
    }

    refresh() {
      this.refreshSlider();
    }
  }

  // Expose globally like carousel.js
  window.RelatedProductsSlider = RelatedProductsSlider;
  customElements.define('related-products-slider', RelatedProductsSlider);
}

// Related Products Recommendations Component
if (!customElements.get('related-products-recommendations')) {
  class RelatedProductsRecommendations extends HTMLElement {
    constructor() {
      super();
      this.loaded = false;
    }

    connectedCallback() {
      this.setupIntersectionObserver();
    }

    setupIntersectionObserver() {
      const handleIntersection = (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      };

      new IntersectionObserver(handleIntersection.bind(this), {
        rootMargin: '0px 0px 400px 0px'
      }).observe(this);
    }

    loadRecommendations() {
      if (this.loaded) return;
      
      const url = this.dataset.url;
      if (!url) return;

      fetch(url)
        .then(response => response.text())
        .then(text => {
          const html = document.createElement('div');
          html.innerHTML = text;
          
          const recommendations = html.querySelector('related-products-recommendations');
          
          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
            this.loaded = true;
            this.classList.add('product-recommendations--loaded');
            
            // Initialize slider after content is loaded
            this.initSlider();
          }

          // Handle slideshow-component fallback
          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }

          if (html.querySelector('.grid__item')) {
            this.classList.add('product-recommendations--loaded');
          }
        })
        .catch(error => {
          console.error('Failed to load recommendations:', error);
        });
    }

    initSlider() {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          const slider = this.querySelector('related-products-slider');
          if (slider && typeof slider.init === 'function') {
            // Ensure Splide is loaded before initializing
            if (typeof Splide !== 'undefined') {
              slider.init();
            } else {
              // Wait for Splide to load
              const checkSplide = setInterval(() => {
                if (typeof Splide !== 'undefined') {
                  clearInterval(checkSplide);
                  slider.init();
                }
              }, 100);
            }
          }
        }, 100);
      });
    }

    disconnectedCallback() {
      const slider = this.querySelector('related-products-slider');
      if (slider && typeof slider.destroy === 'function') {
        slider.destroy();
      }
    }
  }

  customElements.define('related-products-recommendations', RelatedProductsRecommendations);
}

// Fallback initialization for cases where recommendations are already rendered
document.addEventListener('DOMContentLoaded', function() {
  // Ensure Splide is loaded
  if (typeof Splide === 'undefined') {
    console.warn('Splide library not loaded');
    return;
  }

  // Check for already rendered sliders
  const existingSliders = document.querySelectorAll('related-products-slider:not(.is-initialized)');
  
  existingSliders.forEach(slider => {
    const slides = slider.querySelectorAll('.splide__slide');
    if (slides.length > 0 && typeof slider.init === 'function') {
      slider.init();
    }
  });
});

// Handle Shopify section rendering events
document.addEventListener('shopify:section:load', function(event) {
  const section = event.target;
  const slider = section.querySelector('related-products-slider');
  
  if (slider && typeof slider.init === 'function') {
    // Destroy existing instance first
    if (typeof slider.destroy === 'function') {
      slider.destroy();
    }
    
    setTimeout(() => {
      slider.init();
    }, 100);
  }
});

document.addEventListener('shopify:section:unload', function(event) {
  const section = event.target;
  const slider = section.querySelector('related-products-slider');
  
  if (slider && typeof slider.destroy === 'function') {
    slider.destroy();
  }
});

// Handle Shopify section select events (in theme editor)
document.addEventListener('shopify:section:select', function(event) {
  const section = event.target;
  const slider = section.querySelector('related-products-slider');
  
  if (slider && slider.splide && typeof slider.refresh === 'function') {
    slider.refresh();
  }
});